import Phaser from 'phaser';
import { FpsMonitor } from '../utils/FpsMonitor';
import { GameSettings } from '../utils/GameSettings';
import { NetworkService } from '../services/NetworkService';

export class MainMenuScene extends Phaser.Scene {
  private fpsMonitor!: FpsMonitor;
  private settingsManager!: GameSettings;
  private lastFpsCheck: number = 0;
  private fpsCheckInterval: number = 2000; // Check every 2 seconds
  private networkService: NetworkService = NetworkService.getInstance();

  constructor() {
    super('MainMenuScene');
  }

  init(): void {
    console.log('MainMenuScene init method called');
    
    // Check for and remove any leftover gradient textures
    if (this.textures.exists('gradientTexture')) {
      console.log('Found leftover gradient texture in MainMenuScene init, removing it');
      this.textures.remove('gradientTexture');
    }
  }

  create(): void {
    console.log('MainMenuScene create method called');
    
    // Disconnect from the server if we're coming from a multiplayer game
    if (this.networkService.isConnected()) {
      console.log('Disconnecting from server in MainMenuScene');
      this.networkService.destroy();
    }
    
    // Check for and remove any leftover gradient textures
    if (this.textures.exists('gradientTexture')) {
      console.log('Found leftover gradient texture in MainMenuScene, removing it');
      this.textures.remove('gradientTexture');
    }
    
    // Initialize settings manager
    this.settingsManager = GameSettings.getInstance();
    
    // Clear any existing FPS monitors first
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text) {
        const text = child as Phaser.GameObjects.Text;
        if (text.text.includes('FPS:')) {
          console.log('Clearing existing FPS monitor before creating a new one');
          text.destroy();
        }
      }
    });
    
    // Create the FPS monitor
    this.fpsMonitor = new FpsMonitor(this);
    
    // Update the FPS display based on settings
    const showFps = this.settingsManager.getSettings().showFps;
    this.fpsMonitor.toggleDisplay(showFps);
    
    // Listen for FPS display toggle events
    this.events.on('toggleFpsDisplay', (show: boolean) => {
      if (this.fpsMonitor) {
        console.log(`MainMenuScene received toggleFpsDisplay event: ${show}`);
        this.fpsMonitor.toggleDisplay(show);
      }
    });
    
    // Create title text
    const title = this.add.text(400, 100, 'CYTOPOD', {
      fontSize: '64px',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    // Create subtitle
    const subtitle = this.add.text(400, 180, 'Cell Biology Simulation', {
      fontSize: '24px',
      color: '#ffffff'
    }).setOrigin(0.5);
    
    // Create buttons for each evolutionary stage
    this.createEvolutionStageButtons();
    
    // Add attribution link
    this.createAttributionLink();
    
    // Listen for window resize events
    this.events.on('windowResized', this.handleWindowResize, this);
    
    // Add animation to title
    this.tweens.add({
      targets: title,
      y: 120,
      duration: 2000,
      ease: 'Sine.inOut',
      yoyo: true,
      repeat: -1
    });
    
    // Update UI positions based on initial window size
    this.handleWindowResize({
      width: this.scale.width,
      height: this.scale.height
    });
  }
  
  private createEvolutionStageButtons(): void {
    // Create start buttons for each evolutionary stage
    const buttonStyle = {
      fontSize: '24px',
      color: '#ffffff',
      backgroundColor: '#1a6dd7',
      padding: { x: 20, y: 10 },
      stroke: '#000000',
      strokeThickness: 1
    };
    
    // Stage 1: Protocell
    // ! Temporarily disabled
    // const protocellButton = this.add.text(400, 280, 'Stage 1: Early Protocell', buttonStyle)
    //   .setOrigin(0.5)
    //   .setInteractive({ useHandCursor: true })
    //   .on('pointerover', () => protocellButton.setStyle({ color: '#bbbbff' }))
    //   .on('pointerout', () => protocellButton.setStyle({ color: '#ffffff' }))
    //   .on('pointerdown', () => {
    //     console.log('Protocell stage button clicked');
    //     this.performCleanup();
    //     this.scene.stop('MainMenuScene');
    //     this.scene.start('ProtocellScene');
    //   });
    
    // Stage 2: Cytoskeleton
    // ! Temporarily disabled
    // const cytoskeletonButton = this.add.text(400, 350, 'Stage 2: Cytoskeleton', buttonStyle)
    //   .setOrigin(0.5)
    //   .setInteractive({ useHandCursor: true })
    //   .on('pointerover', () => cytoskeletonButton.setStyle({ color: '#bbbbff' }))
    //   .on('pointerout', () => cytoskeletonButton.setStyle({ color: '#ffffff' }))
    //   .on('pointerdown', () => {
    //     console.log('Cytoskeleton stage button clicked');
    //     this.performCleanup();
    //     this.scene.stop('MainMenuScene');
    //     this.scene.start('CytoskeletonScene');
    //   });
    
    // Stage 3: Complex Cell (GameScene)
    // const complexCellButton = this.add.text(400, 420, 'Stage 3: Complex Cell', buttonStyle)
    //   .setOrigin(0.5)
    //   .setInteractive({ useHandCursor: true })
    //   .on('pointerover', () => complexCellButton.setStyle({ color: '#bbbbff' }))
    //   .on('pointerout', () => complexCellButton.setStyle({ color: '#ffffff' }))
    //   .on('pointerdown', () => {
    //     console.log('Complex cell stage button clicked');
    //     this.performCleanup();
    //     this.scene.stop('MainMenuScene');
    //     this.scene.start('GameScene');
    //   });

    // Multiplayer(GameScene)
    const multiplayerButton = this.add.text(
      this.cameras.main.width / 2,
      280, // Position after other buttons
      'Multiplayer',
      buttonStyle
    ).setOrigin(0.5);

    multiplayerButton.setInteractive({ useHandCursor: true });
    multiplayerButton.on('pointerover', () => {
      multiplayerButton.setStyle({ color: '#ffffff', backgroundColor: '#1a8cd7' });
    });
    multiplayerButton.on('pointerout', () => {
      multiplayerButton.setStyle({ color: '#ffffff', backgroundColor: '#1a6dd7' });
    });
    multiplayerButton.on('pointerdown', () => {
      this.scene.start('LobbyScene');
    });
    
    // Settings Button
    // const settingsButton = this.add.text(400, 490, 'Settings', buttonStyle)
    const settingsButton = this.add.text(400, 350, 'Settings', buttonStyle)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .on('pointerover', () => settingsButton.setStyle({ color: '#bbbbff' }))
      .on('pointerout', () => settingsButton.setStyle({ color: '#ffffff' }))
      .on('pointerdown', () => {
        console.log('Settings button clicked');
        // Launch the settings scene as an overlay
        this.scene.launch('SettingsScene');
        // Pause the main menu while settings is active
        this.scene.pause('MainMenuScene');
      });
  }
  
  /**
   * Creates an attribution link in the bottom right corner
   */
  private createAttributionLink(): void {
    const attribution = document.createElement('a');
    attribution.href = 'https://x.com/illyakonovalov';
    attribution.target = '_blank';
    attribution.textContent = 'by @illyakonovalov';
    attribution.style.fontFamily = 'system-ui, sans-serif';
    attribution.style.position = 'fixed';
    attribution.style.bottom = '-1px';
    attribution.style.left = '-1px';
    attribution.style.padding = '7px';
    attribution.style.fontSize = '14px';
    attribution.style.fontWeight = 'bold';
    attribution.style.background = '#fff';
    attribution.style.color = '#000';
    attribution.style.textDecoration = 'none';
    attribution.style.borderTopRightRadius = '12px';
    attribution.style.zIndex = '10000';
    attribution.style.border = '1px solid #fff';
    
    // If there's already an attribution link, remove it first
    const existingAttribution = document.getElementById('attribution-link');
    if (existingAttribution) {
      existingAttribution.remove();
    }
    
    // Add ID for easy reference
    attribution.id = 'attribution-link';
    
    // Add to DOM
    document.body.appendChild(attribution);
  }
  
  // Add a Phaser update method to continuously enforce FPS settings
  update(time: number): void {
    // Periodically check and enforce FPS settings
    if (time > this.lastFpsCheck + this.fpsCheckInterval) {
      this.lastFpsCheck = time;
      
      // Get current settings
      const settings = this.settingsManager.getSettings();
      
      // Apply FPS setting to ensure it sticks
      if (this.game && this.game.loop && this.game.loop.targetFps !== settings.fps) {
        this.game.loop.targetFps = settings.fps;
        console.log(`MainMenu: Re-enforced FPS setting: ${settings.fps}`);
      }
      
      // Check if FPS monitor should be visible
      if (this.fpsMonitor && settings.showFps !== this.fpsMonitor.isVisible()) {
        console.log(`Fixing FPS display visibility: settings=${settings.showFps}, current=${this.fpsMonitor.isVisible()}`);
        this.fpsMonitor.toggleDisplay(settings.showFps);
      }
    }
  }

  // Clean up resources when shutting down
  shutdown(): void {
    console.log('MainMenuScene shutdown method called');
    
    // Kill all tweens
    this.tweens.killAll();
    
    // Perform final cleanup
    this.performCleanup();
    
    if (this.fpsMonitor) {
      this.fpsMonitor.destroy();
    }
    
    // Remove event listeners
    this.events.off('toggleFpsDisplay');
    this.events.off('windowResized', this.handleWindowResize);
    
    // Remove the attribution link when leaving the scene
    const attributionLink = document.getElementById('attribution-link');
    if (attributionLink) {
      attributionLink.remove();
      console.log('Removed attribution link in MainMenuScene shutdown');
    }
    
    console.log('MainMenuScene shutdown complete');
  }
  
  // Helper method for cleanup
  private performCleanup(): void {
    console.log('MainMenuScene performing cleanup');
    
    // Remove any leftover textures
    if (this.textures.exists('gradientTexture')) {
      console.log('Removing gradient texture in MainMenuScene cleanup');
      this.textures.remove('gradientTexture');
    }
    
    console.log('MainMenuScene cleanup complete');
  }

  /**
   * Handle window resize event
   */
  handleWindowResize(size: { width: number, height: number }): void {
    console.log(`MainMenuScene handling window resize: ${size.width}x${size.height}`);
    
    // Recenter and reposition UI elements
    const centerX = size.width / 2;
    
    // Update any UI elements that need repositioning
    this.children.list.forEach(child => {
      if (child instanceof Phaser.GameObjects.Text) {
        // Check if this is one of our centered texts
        if (child.originX === 0.5) {
          // This is a centered element, update its x position
          child.setX(centerX);
        }
      }
    });
  }
} 